/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useReducer, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Label, Spinner, ToggleSwitch } from "flowbite-react";
import { ICustomer } from "../../../../types";
import { Dialog, SelectCountry } from "@components";
import axios from "axios";
import { handleNestedField } from "../../../../helpers/handleNestedFields";
import {
  getPublicFormInputClass,
  getPublicFormLabelClass,
} from "../publicFormFieldClasses";

// Base URL for Cloud API
const baseUrl: string =
  process.env.REACT_APP_CLOUD_API_URL || "http://localhost:8080";

export interface PublicCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: Partial<ICustomer>) => void;
  data: Partial<ICustomer>;
  config?: any;
  accessToken?: string;
  workspaceId?: string;
}

interface CustomerAction {
  type: string;
  payload: { key: string; value: any };
}

const customerReducer = (
  state: Partial<ICustomer>,
  action: CustomerAction,
): Partial<ICustomer> => {
  switch (action.type) {
    case "UPDATE_FIELD":
      return handleNestedField(state, action.payload.key, action.payload.value);
    default:
      return state;
  }
};

const PublicCustomerForm: React.FC<
  Omit<PublicCustomerModalProps, "isOpen">
> = ({ onConfirm, data, config, accessToken, workspaceId }) => {
  const { t } = useTranslation(["order_form"]);
  // Form state
  const [customer, dispatch] = useReducer(customerReducer, data);
  const [isCompany, setIsCompany] = useState<boolean>(data.isCompany ?? true);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Address search state (for individuals)
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [placeId, setPlaceId] = useState<string | null>(null);

  // Company search state (for businesses)
  const [companySearchResults, setCompanySearchResults] = useState<any[]>([]);
  const [showCompanyResults, setShowCompanyResults] = useState(false);
  const [isSearchingCompany, setIsSearchingCompany] = useState(false);

  // Debounce refs
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const companyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const DEBOUNCE_DELAY = 500;

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    dispatch({
      type: "UPDATE_FIELD",
      payload: { key: "isCompany", value: isCompany },
    });
  }, [isCompany]);

  // Fetch address suggestions from backend
  const fetchAddressSuggestions = async (query: string) => {
    setIsSearching(true);
    try {
      const headers: Record<string, string> = {};
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      if (workspaceId) headers["X-Workspace-Id"] = workspaceId;

      const response = await axios.post(
        `${baseUrl}/api/location/autocomplete`,
        { addressLine: query },
        { headers },
      );
      const addresses = response.data?.data || response.data?.addresses || [];
      setAddressSuggestions(addresses);
    } catch (err) {
      console.error("Failed to fetch address suggestions", err);
      setAddressSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSuggestion = async (suggestion: any) => {
    setIsSearching(true);

    try {
      const headers: Record<string, string> = {};
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      if (workspaceId) headers["X-Workspace-Id"] = workspaceId;

      const response = await axios.post(
        `${baseUrl}/api/location/details`,
        { placeId: suggestion.placeId },
        { headers },
      );

      const address = response.data?.data || response.data;
      if (address) {
        const updates = [
          {
            key: "primaryAddress.addressLine",
            value: address.addressLine || "",
          },
          { key: "primaryAddress.postCode", value: address.postCode || "" },
          { key: "primaryAddress.city", value: address.city || "" },
        ];

        updates.forEach((update) => {
          dispatch({ type: "UPDATE_FIELD", payload: update });
        });

        setPlaceId(address.placeId || suggestion.placeId || null);
      }
    } catch (error) {
      console.error("Address details fetch failed:", error);
    } finally {
      setIsSearching(false);
      setAddressSuggestions([]);
      setErrors((prev) => ({ ...prev, addressLine: false }));
    }
  };

  // BRREG company search
  const fetchCompanyData = async (name: string): Promise<void> => {
    if (!name || name.length < 2) {
      clearCompanyResults();
      return;
    }

    setIsSearchingCompany(true);

    try {
      const url = `https://data.brreg.no/enhetsregisteret/api/enheter?size=10&navn=${encodeURIComponent(name)}`;
      const response = await fetch(url, { method: "GET" });

      if (response.ok) {
        const data = await response.json();
        const companies = data._embedded?.enheter || [];
        setCompanySearchResults(companies);
        setShowCompanyResults(companies.length > 0);
      } else {
        clearCompanyResults();
      }
    } catch (error) {
      console.error("BRREG search failed:", error);
      clearCompanyResults();
    } finally {
      setIsSearchingCompany(false);
    }
  };

  const clearCompanyResults = () => {
    setCompanySearchResults([]);
    setShowCompanyResults(false);
  };

  const handleSelectCompany = (company: any) => {
    const updates = [
      { key: "name", value: company.navn || "" },
      { key: "organizationNumber", value: company.organisasjonsnummer || "" },
      {
        key: "primaryAddress.addressLine",
        value: company.forretningsadresse?.adresse?.[0] || "",
      },
      {
        key: "primaryAddress.postCode",
        value: company.forretningsadresse?.postnummer || "",
      },
      {
        key: "primaryAddress.city",
        value: company.forretningsadresse?.poststed || "",
      },
      {
        key: "primaryAddress.countryCode",
        value: company.forretningsadresse?.landkode || "NO",
      },
    ];

    // Apply all updates
    updates.forEach((update) => {
      dispatch({ type: "UPDATE_FIELD", payload: update });
    });

    // Clear search state and errors
    clearCompanyResults();
    setErrors((prev) => ({
      ...prev,
      name: false,
      organizationNumber: false,
      addressLine: false,
      postCode: false,
      city: false,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, boolean> = {};

    // Company validation
    if (isCompany) {
      if (!customer.name || customer.name.trim() === "") {
        newErrors.name = true;
      }
      if (
        !customer.organizationNumber ||
        customer.organizationNumber.trim() === ""
      ) {
        newErrors.organizationNumber = true;
      }
      if (!customer.invoiceEmail || customer.invoiceEmail.trim() === "") {
        newErrors.invoiceEmail = true;
      }
    } else {
      if (!customer.firstName || customer.firstName.trim() === "") {
        newErrors.firstName = true;
      }
      if (!customer.lastName || customer.lastName.trim() === "") {
        newErrors.lastName = true;
      }
    }

    // Common validation
    if (
      !customer.primaryAddress?.addressLine ||
      customer.primaryAddress.addressLine.trim() === ""
    ) {
      newErrors.addressLine = true;
    }
    if (
      !customer.primaryAddress?.postCode ||
      customer.primaryAddress.postCode.trim() === ""
    ) {
      newErrors.postCode = true;
    }
    if (
      !customer.primaryAddress?.city ||
      customer.primaryAddress.city.trim() === ""
    ) {
      newErrors.city = true;
    }
    if (!customer.contactEmail || customer.contactEmail.trim() === "") {
      newErrors.contactEmail = true;
    }
    if (!customer.phoneNumber || customer.phoneNumber.trim() === "") {
      newErrors.phoneNumber = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (validateForm()) {
      onConfirm(customer);
    }
  };

  const handleFieldChange = (key: string, value: any) => {
    dispatch({ type: "UPDATE_FIELD", payload: { key, value } });

    // Trigger appropriate search based on field and customer type
    if (key === "name" && isCompany) {
      handleCompanyNameChange(value);
    } else if (key === "primaryAddress.addressLine" && !isCompany) {
      handleAddressChange(value);
    }

    // Clear validation errors
    if (hasSubmitted) {
      const fieldName = key.split(".").pop() || key;
      setErrors((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleCompanyNameChange = (name: string) => {
    if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
    companyDebounceRef.current = setTimeout(() => {
      fetchCompanyData(name);
    }, DEBOUNCE_DELAY);
  };

  const handleAddressChange = (address: string) => {
    setPlaceId(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (address.trim().length >= 3) {
        fetchAddressSuggestions(address);
      } else {
        setAddressSuggestions([]);
      }
    }, DEBOUNCE_DELAY);
  };

  const inputClass = (fieldName: string) =>
    getPublicFormInputClass(errors, fieldName);

  const labelClass = (fieldName: string) =>
    getPublicFormLabelClass(errors, fieldName);

  return (
    <form
      onSubmit={handleSubmit}
      id="public-customer-form"
      className="space-y-4"
    >
      <p className="text-sm text-gray-600">
        {t("public_form.common.customer_modal_description")}
      </p>

      <div>
        <ToggleSwitch
          id="isCompany"
          checked={isCompany}
          label={t("public_form.common.is_company")}
          onChange={setIsCompany}
          color="green"
        />
      </div>

      {isCompany && (
        <div className="relative">
          <Label
            htmlFor="name"
            value={t("public_form.common.company_name_required")}
            className={labelClass("name")}
          />
          <div className="relative">
            <input
              id="name"
              type="text"
              placeholder={t("public_form.common.company_name")}
              className={inputClass("name")}
              value={customer.name || ""}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              autoComplete="off"
            />
            {isSearchingCompany && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Spinner size="sm" />
              </div>
            )}
          </div>
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">
              {t("public_form.common.company_name_required_error")}
            </p>
          )}

          {/* BRREG company suggestions dropdown */}
          {showCompanyResults && companySearchResults.length > 0 && (
            <div className="absolute w-full z-50">
              <ul className="border border-gray-300 max-h-60 overflow-y-auto mt-1 bg-white rounded-lg shadow-lg">
                <li className="px-3 py-2 text-sm text-gray-500 border-b border-gray-100 bg-gray-50">
                  Velg firma fra Brønnøysundregistrene
                </li>
                {companySearchResults.map((company: any, idx: number) => (
                  <li
                    key={company.organisasjonsnummer || idx}
                    className="px-3 py-2.5 hover:bg-indigo-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-0"
                    onMouseDown={() => handleSelectCompany(company)}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">
                        {company.navn}
                      </span>
                      <span className="text-sm text-gray-500">
                        Org.nr: {company.organisasjonsnummer}
                      </span>
                      {company.forretningsadresse && (
                        <span className="text-xs text-muted">
                          {company.forretningsadresse.adresse?.[0]},{" "}
                          {company.forretningsadresse.postnummer}{" "}
                          {company.forretningsadresse.poststed}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!isCompany && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label
              htmlFor="firstName"
              value={t("public_form.common.first_name_required")}
              className={labelClass("firstName")}
            />
            <input
              id="firstName"
              type="text"
              className={inputClass("firstName")}
              placeholder={t("public_form.common.first_name")}
              value={customer.firstName || ""}
              onChange={(e) => handleFieldChange("firstName", e.target.value)}
              autoComplete="nope"
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600">
                {t("public_form.common.first_name_required_error")}
              </p>
            )}
          </div>
          <div>
            <Label
              htmlFor="lastName"
              value={t("public_form.common.last_name_required")}
              className={labelClass("lastName")}
            />
            <input
              id="lastName"
              type="text"
              className={inputClass("lastName")}
              placeholder={t("public_form.common.last_name")}
              value={customer.lastName || ""}
              onChange={(e) => handleFieldChange("lastName", e.target.value)}
              autoComplete="nope"
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-600">
                {t("public_form.common.last_name_required_error")}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="relative">
        <Label
          htmlFor="addressLine"
          value={t("public_form.common.address_required")}
          className={labelClass("addressLine")}
        />
        <div className="relative">
          <input
            id="addressLine"
            type="text"
            placeholder={t("public_form.common.address_placeholder")}
            className={`${inputClass("addressLine")} ${!isCompany && !placeId && customer.primaryAddress?.addressLine ? "border-yellow-400" : ""}`}
            value={customer.primaryAddress?.addressLine || ""}
            onChange={(e) =>
              handleFieldChange("primaryAddress.addressLine", e.target.value)
            }
            autoComplete="off"
          />
          {!isCompany && !placeId && customer.primaryAddress?.addressLine && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-yellow-500 text-sm">
              ↓ {t("public_form.common.select_from_list")}
            </div>
          )}
        </div>
        {errors.addressLine && (
          <p className="mt-1 text-sm text-red-600">
            {t("public_form.common.address_required_error")}
          </p>
        )}

        {/* Address suggestions dropdown - only for individuals */}
        {!isCompany && (addressSuggestions.length > 0 || isSearching) && (
          <div className="absolute w-full z-50">
            <ul className="border border-gray-300 max-h-60 overflow-y-auto mt-1 bg-white rounded-lg shadow-lg">
              {isSearching && (
                <li className="flex justify-center py-3 border-b border-gray-100">
                  <Spinner size="sm" />
                </li>
              )}
              {!isSearching && addressSuggestions.length > 0 && (
                <li className="px-3 py-2 text-sm text-gray-500 border-b border-gray-100 bg-gray-50">
                  {t("public_form.common.select_address_to_continue")}
                </li>
              )}
              {addressSuggestions.map((s: any, idx: number) => (
                <li
                  key={s.placeId || idx}
                  className="px-3 py-2.5 hover:bg-indigo-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-0"
                  onMouseDown={() => handleSelectSuggestion(s)}
                >
                  {s.addressLine}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label
            htmlFor="postCode"
            value={t("public_form.common.postcode_required")}
            className={labelClass("postCode")}
          />
          <input
            id="postCode"
            type="text"
            placeholder={t("public_form.common.postcode_placeholder")}
            className={inputClass("postCode")}
            value={customer.primaryAddress?.postCode || ""}
            onChange={(e) =>
              handleFieldChange("primaryAddress.postCode", e.target.value)
            }
          />
          {errors.postCode && (
            <p className="mt-1 text-sm text-red-600">
              {t("public_form.common.postcode_required_error")}
            </p>
          )}
        </div>
        <div>
          <Label
            htmlFor="city"
            value={t("public_form.common.city_required")}
            className={labelClass("city")}
          />
          <input
            id="city"
            type="text"
            placeholder={t("public_form.common.city_placeholder")}
            className={inputClass("city")}
            value={customer.primaryAddress?.city || ""}
            onChange={(e) =>
              handleFieldChange("primaryAddress.city", e.target.value)
            }
          />
          {errors.city && (
            <p className="mt-1 text-sm text-red-600">
              {t("public_form.common.city_required_error")}
            </p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label
            htmlFor="countryCode"
            value={t("public_form.common.country")}
          />
          <SelectCountry
            id="countryCode"
            defaultValue={customer.primaryAddress?.countryCode || "NO"}
            onSelectChanged={(c: string) =>
              handleFieldChange("primaryAddress.countryCode", c)
            }
          />
        </div>
        {isCompany && (
          <div>
            <Label
              htmlFor="organizationNumber"
              value={t("public_form.common.organization_number_required")}
              className={labelClass("organizationNumber")}
            />
            <input
              id="organizationNumber"
              type="text"
              placeholder={t("public_form.common.organization_number")}
              className={inputClass("organizationNumber")}
              value={customer.organizationNumber || ""}
              onChange={(e) =>
                handleFieldChange("organizationNumber", e.target.value)
              }
            />
            {errors.organizationNumber && (
              <p className="mt-1 text-sm text-red-600">
                {t("public_form.common.organization_number_required_error")}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="careOf" value={t("public_form.common.care_of")} />
          <input
            id="careOf"
            type="text"
            placeholder={t("public_form.common.care_of_placeholder")}
            className={inputClass("careOf")}
            value={customer.primaryAddress?.careOf || ""}
            onChange={(e) =>
              handleFieldChange("primaryAddress.careOf", e.target.value)
            }
          />
        </div>
        <div>
          <Label
            htmlFor="contactEmail"
            value={t("public_form.common.email_required")}
            className={labelClass("contactEmail")}
          />
          <input
            id="contactEmail"
            type="email"
            placeholder={t("public_form.common.email_placeholder")}
            className={inputClass("contactEmail")}
            value={customer.contactEmail || ""}
            onChange={(e) => handleFieldChange("contactEmail", e.target.value)}
          />
          {errors.contactEmail && (
            <p className="mt-1 text-sm text-red-600">
              {t("public_form.common.email_required_error")}
            </p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label
            htmlFor="invoiceEmail"
            value={
              isCompany
                ? t("public_form.common.invoice_email_required")
                : t("public_form.common.invoice_email")
            }
            className={labelClass("invoiceEmail")}
          />
          <input
            id="invoiceEmail"
            type="email"
            placeholder={t("public_form.common.email_placeholder")}
            className={inputClass("invoiceEmail")}
            value={customer.invoiceEmail || ""}
            onChange={(e) => handleFieldChange("invoiceEmail", e.target.value)}
          />
          {errors.invoiceEmail && (
            <p className="mt-1 text-sm text-red-600">
              {t("public_form.common.invoice_email_required_error")}
            </p>
          )}
        </div>
        <div>
          <Label
            htmlFor="phoneNumber"
            value={t("public_form.common.phone_required")}
            className={labelClass("phoneNumber")}
          />
          <input
            id="phoneNumber"
            type="tel"
            placeholder={t("public_form.common.phone_placeholder")}
            className={inputClass("phoneNumber")}
            value={customer.phoneNumber || ""}
            onChange={(e) => handleFieldChange("phoneNumber", e.target.value)}
          />
          {errors.phoneNumber && (
            <p className="mt-1 text-sm text-red-600">
              {t("public_form.common.phone_required_error")}
            </p>
          )}
        </div>
      </div>
    </form>
  );
};

export const PublicCustomerModal: React.FC<PublicCustomerModalProps> = (
  props,
) => {
  const { t } = useTranslation(["order_form", "common"]);

  return (
    <Dialog
      title={t("public_form.common.new_customer")}
      show={props.isOpen}
      toggleModal={props.onClose}
      buttonConfirmText={t("public_form.common.save")}
      buttonCloseText={t("common:close")}
      confirmButtonColor="primary"
      confirmButton={() => {
        document
          .getElementById("public-customer-form")
          ?.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true }),
          );
      }}
      content={<PublicCustomerForm {...props} />}
    />
  );
};

export default PublicCustomerModal;
